import React from 'react';
import type { ComponentConfig } from '@puckeditor/core';
import styled from 'styled-components';

import { ColorRoleField } from '../fields/widgets/color-role-field';
import { SURFACE_COLOR_FIELD_AI_SCHEMA, SurfaceFillField } from '../fields/widgets/surface-color-field';
import {
  BACKGROUND_OPACITY_FIELD,
  DEFAULT_BACKGROUND_OPACITY,
  resolveBackgroundColorWithOpacity,
  resolveBackgroundOpacityFields,
} from '../fields/background-opacity';
import { createSpacingFields } from '../fields/spacing-fields';
import { CmsHeadingRichTextMenu, CmsRichTextMenu, cmsRichTextSelector } from '../fields/widgets/richtext-menu';
import { interpolateHtml } from '../interpolate-html';
import { getCmsVariables } from '../interpolation';
import { headingDocument } from './heading-document';
import { normalizeHeadingEditorHtml, semanticHeadingHtml } from './heading-html';
import {
  BLOCK_WIDTHS,
  FONT_ROLES,
  HEADING_LINE_HEIGHT,
  HEADING_LINE_HEIGHTS,
  HEADING_PRESETS,
  lookup,
  MAX_WIDTHS,
  NO_SPACING,
  resolveTextColor,
  spacingToCss,
  SURFACE_COLOR_ROLES,
  TEXT_COLOR_ROLES,
} from '../fields/tokens';
import type {
  BlockWidthOption,
  BoxSpacing,
  FontRole,
  HeadingLineHeight,
  HeadingPreset,
  MaxWidthOption,
} from '../fields/tokens';

export const DEFAULT_HEADING_HTML = '<p>Heading</p>';

const HEADING_LEVELS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] as const;
const SYSTEM_PRESET = 'system';
type HeadingLevel = typeof HEADING_LEVELS[number];
type HeadingSize = HeadingPreset | typeof SYSTEM_PRESET;

export type HeadingProps = {
  text: string;
  /** Semantic tag, independent from the visual preset. */
  level: HeadingLevel;
  /** Visual size, or `system` to inherit the storefront heading default. */
  preset: HeadingSize;
  /** Line-height multiplier, or `system` to inherit the storefront heading default. */
  lineHeight: HeadingLineHeight;
  font: FontRole;
  color: string;
  /** Either a surface role key (e.g. `'surface'`) or a custom hex (e.g. `'#ff0000'`). */
  background: string;
  backgroundOpacity: number;
  width: BlockWidthOption;
  maxWidth: MaxWidthOption;
  padding: BoxSpacing;
};

/**
 * The frame owns box styling while its one child owns semantics. In the editor that child is
 * Puck's live Tiptap node; in production it is the canonical semantic heading HTML above.
 */
const StyledHeading = styled.div`
  margin: 0;
  background: var(--cms-heading-background, ${SURFACE_COLOR_ROLES.none});
  color: var(--cms-heading-color, ${TEXT_COLOR_ROLES.text});
  font-family: var(--cms-heading-font-family, ${FONT_ROLES.heading});
  font-size: var(--cms-heading-font-size, ${HEADING_PRESETS.h2.fontSize});
  font-weight: var(--cms-heading-font-weight, ${HEADING_PRESETS.h2.fontWeight});
  line-height: var(--cms-heading-line-height, ${HEADING_LINE_HEIGHT});
  width: var(--cms-heading-width, ${BLOCK_WIDTHS.fill});
  max-width: var(--cms-heading-max-width, ${MAX_WIDTHS.none});
  padding: var(--cms-heading-padding, 0px);

  p,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    color: inherit;
    font: inherit;
    margin: 0;
  }

  a {
    color: var(--dcm-cms-heading-link-color, var(--dcm-color-link, currentColor));
    text-decoration: underline;
  }
`;

const SPACING_FIELDS = createSpacingFields();

const SIZE_OPTIONS: { label: string; value: HeadingSize }[] = [
  { label: 'System default', value: SYSTEM_PRESET },
  { label: 'Display', value: 'h1' },
  { label: 'Heading 1', value: 'h2' },
  { label: 'Heading 2', value: 'h3' },
  { label: 'Heading 3', value: 'h4' },
  { label: 'Heading 4', value: 'h5' },
  { label: 'Eyebrow', value: 'h6' },
];

const LINE_HEIGHT_OPTIONS: { label: string; value: HeadingLineHeight }[] = [
  { label: 'System default', value: 'system' },
  { label: 'Tight', value: 'tight' },
  { label: 'Comfortable', value: 'comfortable' },
  { label: 'Spacious', value: 'spacious' },
];

const LINE_WIDTH_OPTIONS: { label: string; value: MaxWidthOption }[] = [
  { label: 'Full available width', value: 'none' },
  { label: 'Normal', value: 'normal' },
  { label: 'Narrow', value: 'narrow' },
];

export const headingConfig: ComponentConfig<HeadingProps> = {
  label: 'Heading',
  ai: {
    instructions:
      'A rich-text heading for page titles and content hierarchy. Set `level` for document structure (one h1 per page) independently from the visual `preset`. Supports inline formatting, alignment, links, and CMS variable tokens.',
  },
  fields: {
    text: {
      type: 'richtext',
      label: 'Heading content',
      contentEditable: true,
      renderMenu: CmsHeadingRichTextMenu,
      renderInlineMenu: CmsRichTextMenu,
      tiptap: { extensions: [headingDocument], selector: cmsRichTextSelector },
      options: {
        document: false,
        heading: false,
        blockquote: false,
        codeBlock: false,
        hardBreak: false,
        horizontalRule: false,
        listItem: false,
        bulletList: false,
        orderedList: false,
        listKeymap: false,
        link: { openOnClick: false },
      },
    },
    level: {
      type: 'select',
      label: 'Level',
      options: HEADING_LEVELS.map((level) => ({ label: level.toUpperCase(), value: level })),
      ai: {
        schema: { type: 'string', enum: [...HEADING_LEVELS] },
        instructions:
          'HTML heading level, for accessibility and SEO only. Use h1 for the page title (once per page), h2 for sections, h3+ for subsections.',
      },
    },
    preset: {
      type: 'select',
      label: 'Text size',
      options: SIZE_OPTIONS,
      ai: {
        schema: { type: 'string', enum: SIZE_OPTIONS.map(({ value }) => value) },
        instructions:
          "Visual size, independent from `level`. Leave as 'system' to follow the merchant theme; choose a preset only when the design requires it.",
      },
    },
    lineHeight: {
      type: 'select',
      label: 'Line height',
      options: LINE_HEIGHT_OPTIONS,
      ai: {
        schema: { type: 'string', enum: LINE_HEIGHT_OPTIONS.map(({ value }) => value) },
        instructions:
          "Line height multiplier for the heading. Leave as 'system' to inherit the theme default; choose tight, comfortable, or spacious when visual rhythm requires it.",
      },
    },
    font: {
      type: 'radio',
      label: 'Font',
      options: [
        { label: 'Heading', value: 'heading' },
        { label: 'Body', value: 'body' },
      ],
      ai: {
        schema: { type: 'string', enum: ['heading', 'body'] },
        instructions:
          "Which of the merchant's two configured fonts to use. Prefer 'heading' unless the text is deliberately styled as body copy.",
      },
    },
    color: {
      type: 'custom',
      label: 'Text color',
      render: ColorRoleField,
      ai: {
        schema: {
          type: 'string',
          description:
            "Either a theme color role ('text', 'secondary', 'muted', 'inverted', 'link') or a hex value such as #121516. Prefer a role so the heading follows merchant branding.",
        },
      },
    },
    background: {
      type: 'custom',
      label: 'Background',
      render: SurfaceFillField,
      ai: {
        schema: SURFACE_COLOR_FIELD_AI_SCHEMA,
        instructions:
          "Optional fill behind the heading. Set to a surface role ('none', 'surface', 'muted') so it follows merchant branding; use a hex value only when a specific brand color is required.",
      },
    },
    backgroundOpacity: BACKGROUND_OPACITY_FIELD,
    width: {
      type: 'radio',
      label: 'Width',
      options: [
        { label: 'Fit', value: 'fit' },
        { label: 'Fill', value: 'fill' },
      ],
      ai: { schema: { type: 'string', enum: ['fit', 'fill'] } },
    },
    maxWidth: {
      type: 'radio',
      label: 'Line width',
      options: LINE_WIDTH_OPTIONS,
      ai: {
        schema: { type: 'string', enum: ['none', 'normal', 'narrow'] },
        instructions:
          'Constrains line length for readability. Prefer Full available width unless heading wrap needs constraint.',
      },
    },
    padding: {
      type: 'object',
      label: 'Padding',
      objectFields: SPACING_FIELDS,
    },
  },
  resolveFields: (data, { fields }) => resolveBackgroundOpacityFields(data.props.background, fields),
  resolveData: ({ props }) => {
    const normalizedText = normalizeHeadingEditorHtml(props.text);
    return normalizedText === props.text ? { props } : { props: { ...props, text: normalizedText } };
  },
  defaultProps: {
    text: DEFAULT_HEADING_HTML,
    level: 'h2',
    preset: SYSTEM_PRESET,
    lineHeight: 'system',
    font: 'heading',
    color: 'text',
    background: 'none',
    backgroundOpacity: DEFAULT_BACKGROUND_OPACITY,
    width: 'fill',
    maxWidth: 'none',
    padding: NO_SPACING,
  },
  render: ({
    text = DEFAULT_HEADING_HTML,
    level = 'h2',
    preset = SYSTEM_PRESET,
    lineHeight = 'system',
    font = 'heading',
    color = 'text',
    background = 'none',
    backgroundOpacity = DEFAULT_BACKGROUND_OPACITY,
    width = 'fill',
    maxWidth = 'none',
    padding = NO_SPACING,
    puck,
  }) => {
    const content = text as unknown;
    const tag = HEADING_LEVELS.includes(level) ? level : 'h2';
    const sizing = preset === SYSTEM_PRESET ? undefined : lookup(HEADING_PRESETS, preset, HEADING_PRESETS.h2);
    const resolvedLineHeight =
      lineHeight === 'system' ? undefined : lookup(HEADING_LINE_HEIGHTS, lineHeight, undefined);
    const style = {
      '--cms-heading-background': resolveBackgroundColorWithOpacity(background, backgroundOpacity),
      '--cms-heading-color': resolveTextColor(color),
      '--cms-heading-font-family': lookup(FONT_ROLES, font, FONT_ROLES.heading),
      ...(sizing
        ? {
            '--cms-heading-font-size': sizing.fontSize,
            '--cms-heading-font-weight': sizing.fontWeight,
          }
        : {}),
      ...(resolvedLineHeight
        ? {
            '--cms-heading-line-height': resolvedLineHeight,
          }
        : {}),
      '--cms-heading-width': lookup(BLOCK_WIDTHS, width, BLOCK_WIDTHS.fill),
      '--cms-heading-max-width': lookup(MAX_WIDTHS, maxWidth, MAX_WIDTHS.none),
      '--cms-heading-padding': spacingToCss(padding),
    } as React.CSSProperties;

    if (typeof content !== 'string') {
      return puck.isEditing ? <StyledHeading style={style}>{content as React.ReactNode}</StyledHeading> : <></>;
    }

    const interpolatedHtml = interpolateHtml(content, getCmsVariables(puck));
    const renderedHtml = semanticHeadingHtml(interpolatedHtml, tag);
    return renderedHtml ? <StyledHeading style={style} dangerouslySetInnerHTML={{ __html: renderedHtml }} /> : <></>;
  },
};
